import { useQuery } from '@tanstack/react-query';
import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { User, Shield, Trash2, UserCheck, AlertCircle, Loader2 } from 'lucide-react';

const AllUsers = () => {
    const [loadingAdmin, setLoadingAdmin] = useState(null);
    const [loadingDelete, setLoadingDelete] = useState(null);

    const { data: users = [], refetch, isLoading, error } = useQuery({
        queryKey: ['users'],
        queryFn: async () => {
            const res = await fetch('https://doctors-portal-server-ten-iota.vercel.app/users');
            if (!res.ok) {
                throw new Error('Failed to fetch users');
            }
            const data = await res.json();
            return data;
        }
    });

    const handleMakeAdmin = async (id) => {
        setLoadingAdmin(id);
        try {
            const res = await fetch(`https://doctors-portal-server-ten-iota.vercel.app/users/admin/${id}`, {
                method: 'PUT',
                headers: {
                    authorization: `bearer ${localStorage.getItem('accessToken')}`
                }
            });

            if (res.status === 403) {
                toast.error('Failed to make admin - insufficient permissions');
                return;
            }

            if (!res.ok) {
                throw new Error('Failed to update user role');
            }

            const data = await res.json();
            if (data.modifiedCount > 0) {
                toast.success('User promoted to admin successfully!');
                refetch();
            } else {
                toast.error('No changes were made');
            }
        } catch (error) {
            toast.error('Error updating user role');
            console.error('Error making admin:', error);
        } finally {
            setLoadingAdmin(null);
        }
    };

    const handleDeleteUser = async (id, name) => {
        if (!window.confirm(`Are you sure you want to delete user "${name}"? This action cannot be undone.`)) {
            return;
        }

        setLoadingDelete(id);
        try {
            const res = await fetch(`https://doctors-portal-server-ten-iota.vercel.app/users/${id}`, {
                method: 'DELETE',
                headers: {
                    authorization: `bearer ${localStorage.getItem('accessToken')}`
                }
            });

            if (res.status === 403) {
                toast.error('Failed to delete user - insufficient permissions');
                return;
            }

            if (!res.ok) {
                throw new Error('Failed to delete user');
            }

            const data = await res.json();
            if (data.deletedCount > 0) {
                toast.success('User deleted successfully!');
                refetch();
            } else {
                toast.error('No user was deleted');
            }
        } catch (error) {
            toast.error('Error deleting user');
            console.error('Error deleting user:', error);
        } finally {
            setLoadingDelete(null);
        }
    };

    if (isLoading) {
        return (
            <div className="min-h-96 flex items-center justify-center">
                <div className="flex flex-col items-center gap-4">
                    <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                    <p className="text-gray-600">Loading users...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-96 flex items-center justify-center">
                <div className="flex flex-col items-center gap-4 text-center">
                    <AlertCircle className="h-12 w-12 text-red-500" />
                    <div>
                        <h3 className="text-lg font-semibold text-gray-900">Error Loading Users</h3>
                        <p className="text-gray-600 mt-1">Failed to fetch user data. Please try again.</p>
                    </div>
                    <button
                        onClick={() => refetch()}
                        className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
                    >
                        Try Again
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="p-6 bg-gray-50 min-h-screen">
            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6 p-6">
                    <div className="flex items-center gap-3">
                        <User className="h-6 w-6 text-blue-600" />
                        <h1 className="text-2xl font-bold text-gray-900">All Users</h1>
                        <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-sm font-medium">
                            {users.length} users
                        </span>
                    </div>
                    <p className="text-gray-600 mt-2">Manage user roles and permissions</p>
                </div>

                {/* Users Table */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                    {users.length === 0 ? (
                        <div className="p-12 text-center">
                            <User className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                            <h3 className="text-lg font-medium text-gray-900 mb-2">No Users Found</h3>
                            <p className="text-gray-500">There are no users to display at the moment.</p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead>
                                    <tr className="bg-gray-50 border-b border-gray-200">
                                        <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            #
                                        </th>
                                        <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            User
                                        </th>
                                        <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Email
                                        </th>
                                        <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Role
                                        </th>
                                        <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Actions
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200">
                                    {users.map((user, i) => (
                                        <tr key={user._id} className="hover:bg-gray-50 transition-colors">
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {i + 1}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="flex items-center gap-3">
                                                    <div className="h-10 w-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-medium">
                                                        {user.name?.charAt(0).toUpperCase() || 'U'}
                                                    </div>
                                                    <div>
                                                        <p className="text-sm font-medium text-gray-900">
                                                            {user.name || 'Unknown User'}
                                                        </p>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <p className="text-sm text-gray-900">{user.email}</p>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                {user?.role === 'admin' ? (
                                                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                                        <Shield className="h-3 w-3" />
                                                        Admin
                                                    </span>
                                                ) : (
                                                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                                                        <User className="h-3 w-3" />
                                                        User
                                                    </span>
                                                )}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="flex items-center gap-2">
                                                    {user?.role !== 'admin' && (
                                                        <button
                                                            onClick={() => handleMakeAdmin(user._id)}
                                                            disabled={loadingAdmin === user._id}
                                                            className="inline-flex items-center gap-1 px-3 py-1 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white text-xs font-medium rounded-lg transition-colors"
                                                        >
                                                            {loadingAdmin === user._id ? (
                                                                <Loader2 className="h-3 w-3 animate-spin" />
                                                            ) : (
                                                                <UserCheck className="h-3 w-3" />
                                                            )}
                                                            Make Admin
                                                        </button>
                                                    )}
                                                    <button
                                                        onClick={() => handleDeleteUser(user._id, user.name)}
                                                        disabled={loadingDelete === user._id}
                                                        className="inline-flex items-center gap-1 px-3 py-1 bg-red-600 hover:bg-red-700 disabled:bg-red-400 text-white text-xs font-medium rounded-lg transition-colors"
                                                    >
                                                        {loadingDelete === user._id ? (
                                                            <Loader2 className="h-3 w-3 animate-spin" />
                                                        ) : (
                                                            <Trash2 className="h-3 w-3" />
                                                        )}
                                                        Delete
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AllUsers;